<template>
  <div class="pilgrim-form">
    <!-- Основной контейнер -->
    <div class="form-container">
      <!-- Заголовок и описание -->
      <div class="header-section">
        <div class="title-section">
          <h1 class="form-title">{{ $t("pilgrimForm.title") }}</h1>
          <p class="form-description">
            {{ $t("pilgrimForm.description") }}
          </p>
        </div>

        <pilgrim-form-progress
          :current-step="step"
          @change:step="(val) => (step = val)"
        />
      </div>

      <div class="form-content-container">
        <div v-if="step === 1" class="form-content">
          <h2 class="section-title">{{ $t("pilgrimForm.step1Title") }}</h2>

          <div class="form-fields">
            <!-- Поля для ввода -->
            <div class="input-group">
              <input
                type="text"
                class="form-input"
                v-model="formData.lastName"
                :class="{ 'has-value': formData.lastName }"
              />
              <div class="input-label">
                {{ $t("pilgrimForm.fields.lastName") }}
              </div>
            </div>

            <div class="input-group">
              <input
                type="text"
                class="form-input"
                v-model="formData.firstName"
                :class="{ 'has-value': formData.firstName }"
              />
              <div class="input-label">
                {{ $t("pilgrimForm.fields.firstName") }}
              </div>
            </div>

            <div class="input-group">
              <input
                type="text"
                class="form-input"
                v-model="formData.fatherName"
                :class="{ 'has-value': formData.fatherName }"
              />
              <div class="input-label">
                {{ $t("pilgrimForm.fields.fatherName") }}
              </div>
            </div>

            <div class="input-group">
              <input
                class="form-input"
                v-model="formData.age"
                :class="{ 'has-value': formData.age }"
              />
              <div class="input-label">{{ $t("pilgrimForm.fields.age") }}</div>
              <p class="field-hint">{{ $t("pilgrimForm.fields.ageHint") }}</p>
            </div>

            <div class="input-group">
              <select v-model="formData.region_id">
                <option
                  v-for="region in regions"
                  :key="region.id"
                  :value="region.id"
                >
                  {{ region.name }}
                </option>
              </select>
              <div class="input-label">
                {{ $t("pilgrimForm.fields.region") }}
              </div>
              <svg
                class="dropdown-icon"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
              >
                <path
                  d="M7 10L12 15L17 10"
                  stroke="#2A2F3E"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
              <p class="field-hint">
                {{ $t("pilgrimForm.fields.regionHint") }}
              </p>
            </div>

            <div class="input-group">
              <select v-model="formData.hajjYear">
                <option
                  v-for="year in availableYears"
                  :key="year"
                  :value="year"
                >
                  {{ year }}
                </option>
              </select>
              <div class="input-label">
                {{ $t("pilgrimForm.fields.hajjYear") }}
              </div>
              <svg
                class="dropdown-icon"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
              >
                <path
                  d="M7 10L12 15L17 10"
                  stroke="#2A2F3E"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </div>

            <div class="input-group">
              <input
                type="text"
                class="form-input"
                v-model="formData.iin"
                :class="{ 'has-value': formData.iin }"
              />
              <div class="input-label">{{ $t("pilgrimForm.fields.iin") }}</div>
            </div>
          </div>

          <!-- Секция загрузки фото -->
          <div class="photo-upload-section">
            <div class="upload-preview">
              <svg-icon
                v-if="!formData.photo"
                name="user"
                class="user-profile__icon"
              />
              <div v-else class="user-avatar">
                <img :src="photoUrl" alt="user-photo" />
              </div>
            </div>

            <div class="upload-content">
              <div class="upload-info">
                <h3 class="upload-title">
                  {{ $t("pilgrimForm.photoUpload.title") }}
                </h3>
                <p class="upload-description">
                  {{ $t("pilgrimForm.photoUpload.description") }}
                </p>
              </div>

              <div class="upload-button-section">
                <button class="upload-button" @click="triggerFileUpload">
                  <svg
                    class="upload-icon"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                  >
                    <path
                      d="M21 15V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V15"
                      stroke="white"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                    <path
                      d="M17 8L12 3L7 8"
                      stroke="white"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                    <path
                      d="M12 3V15"
                      stroke="white"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                  <span>{{ $t("pilgrimForm.photoUpload.button") }}</span>
                </button>
                <p class="upload-hint">
                  {{ $t("pilgrimForm.photoUpload.hint") }}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div v-if="step === 2" class="form-content">
          <h2 class="section-title">{{ $t("pilgrimForm.step2Title") }}</h2>
          <div class="form-fields">
            <div class="input-group">
              <textarea
                type="text"
                class="form-input"
                v-model="formData.text"
                :placeholder="$t('pilgrimForm.fields.biographyPlaceholder')"
                rows="10"
              ></textarea>
              <p class="field-hint">
                {{ $t("pilgrimForm.fields.biographyHint") }}
              </p>
            </div>
          </div>
        </div>

        <div v-if="step === 3" class="form-content">
          <h2 class="section-title">{{ $t("pilgrimForm.step3Title") }}</h2>
          <div class="form-fields">
            <div class="input-group">
              <textarea
                type="text"
                class="form-input"
                v-model="formData.review"
                :placeholder="$t('pilgrimForm.fields.impressionsPlaceholder')"
                rows="5"
              ></textarea>
              <p class="field-hint">
                {{ $t("pilgrimForm.fields.impressionsHint") }}
              </p>
            </div>
          </div>
          <h2 class="section-title">
            {{ $t("pilgrimForm.step3AdviceTitle") }}
          </h2>
          <div class="form-fields">
            <div class="input-group">
              <textarea
                type="text"
                class="form-input"
                v-model="formData.tips"
                :placeholder="$t('pilgrimForm.fields.advicePlaceholder')"
                rows="5"
              ></textarea>
              <p class="field-hint">
                {{ $t("pilgrimForm.fields.adviceHint") }}
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- Кнопка отправки -->
      <button class="submit-button" @click="submitForm">
        {{
          step === 3
            ? $t("pilgrimForm.buttons.submit")
            : $t("pilgrimForm.buttons.next")
        }}
      </button>
    </div>

    <!-- Кнопка закрытия -->
    <button
      class="close-button"
      @click="() => $store.dispatch('closePilgrimForm')"
    >
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
        <path
          d="M18 6L6 18M6 6L18 18"
          stroke="#042A2E"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    </button>

    <!-- Скрытый input для загрузки файлов -->
    <input
      type="file"
      ref="fileInput"
      accept="image/*"
      style="display: none"
      @change="handleFileUpload"
    />
  </div>
</template>

<script>
import { defineComponent } from "vue";
import PilgrimFormProgress from "./PilgrimFormProgress.vue";
import { geoService, hajiService } from "@/services";
import { ElMessage } from "element-plus";

export default defineComponent({
  name: "PilgrimForm",
  components: {
    PilgrimFormProgress,
  },
  data() {
    return {
      regions: [],
      step: 1,
      formData: {
        lastName: "",
        firstName: "",
        fatherName: "",
        age: "",
        region_id: null,
        hajjYear: null,
        iin: "",
        photo: null,

        text: "",
        review: "",
        tips: "",
      },

      requiredFieldsTips: {
        lastName: "pilgrimForm.validation.lastName",
        firstName: "pilgrimForm.validation.firstName",
        age: "pilgrimForm.validation.age",
        region_id: "pilgrimForm.validation.region",
        hajjYear: "pilgrimForm.validation.hajjYear",
        iin: "pilgrimForm.validation.iin",
        photo: "pilgrimForm.validation.photo",
      },
    };
  },
  computed: {
    photoUrl() {
      return this.formData.photo
        ? URL.createObjectURL(this.formData.photo)
        : null;
    },
    availableYears() {
      const currentYear = new Date().getFullYear();
      const years = [];
      for (let year = currentYear; year >= 1990; year--) {
        years.push(year.toString());
      }
      return years;
    },
  },
  mounted() {
    this.getRegions();
  },
  methods: {
    getRegions() {
      geoService.getPilgrimRegions().then(({ results }) => {
        this.regions = results;
      });
    },
    handleFileUpload(event) {
      const file = event.target.files[0];
      if (file) {
        this.formData.photo = file;
      }
    },
    triggerFileUpload() {
      this.$refs.fileInput.click();
    },
    submitForm() {
      const emptyFields = Object.keys(this.requiredFieldsTips).filter(
        (field) => !this.formData[field]
      );

      if (emptyFields.length > 0) {
        ElMessage.error(this.$t(this.requiredFieldsTips[emptyFields[0]]));
        return;
      }

      if (this.step < 3) {
        this.step++;
      } else {
        this.createHaji();
      }
    },
    createHaji() {
      const hajjDate = new Date(this.formData.hajjYear);
      const hajjDateString = hajjDate.toISOString().split("T")[0];

      const birthDay = new Date(
        String(new Date().getFullYear() - parseInt(this.formData.age))
      );
      const birthDayString = birthDay.toISOString().split("T")[0];

      hajiService
        .createHaji({
          full_name: `${this.formData.lastName} ${this.formData.firstName} ${this.formData.fatherName}`,
          birth_day: birthDayString,
          hajj_date: hajjDateString,
          region_id: this.formData.region_id.toString(),
          iin: this.formData.iin,
          image: this.formData.photo,
          text: this.formData.text,
          review: this.formData.review,
          tips: this.formData.tips,
        })
        .then((response) => {
          console.log(response);
          this.$store.dispatch("closePilgrimForm");
          ElMessage.success(this.$t("pilgrimForm.messages.success"));
        })
        .catch((error) => {
          let errorMessage = this.$t("pilgrimForm.messages.error");
          const errorData = error.response.data;
          if (errorData) {
            const errorKeys = Object.keys(errorData);
            if (errorKeys.length > 0) {
              const firstKey = errorKeys[0];
              if (Array.isArray(errorData[firstKey])) {
                errorMessage = errorData[firstKey][0];
              } else {
                errorMessage = errorData[firstKey];
              }
            }
          }
          ElMessage.error(errorMessage);
        });
    },
  },
});
</script>

<style scoped>
.pilgrim-form {
  @apply min-h-full w-full relative box-border;
}

.form-container {
  @apply w-full min-h-full flex flex-col gap-6;
  .form-content-container {
    @apply min-h-full flex-1 flex flex-col gap-6;
  }
}

/* Заголовок и описание */
.header-section {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.title-section {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.form-title {
  @apply text-xl font-semibold text-black;
}

.form-description {
  @apply text-sm text-zinc-500 leading-5;
}

/* Основная форма */
.form-content {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.section-title {
  @apply text-base text-black leading-5;
}

.form-fields {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.input-group {
  @apply flex flex-col gap-2 relative;
  .form-input,
  select {
    @apply w-full bg-zinc-100 border-none rounded-lg p-4 text-zinc-700;
    @apply text-sm text-zinc-700;
  }

  /* select has value then input label size is 12px and input has value then input label size is 12px */
  select:has(option:checked),
  .form-input.has-value {
    @apply pt-6 pb-2;
    & + .input-label {
      @apply top-[8px];
    }
  }

  .input-label {
    @apply text-sm text-zinc-500 absolute top-[19px] leading-none left-4;
  }
  .dropdown-icon {
    @apply absolute right-3 top-[24px] -translate-y-1/2;
  }
}

select {
  appearance: none; /* Стандартный способ */
  -webkit-appearance: none;
  -moz-appearance: none;
}

.form-input:focus {
  outline: none;
  background: #e8e8e8;
}

.form-input::placeholder {
  color: #888;
}

.field-hint {
  @apply text-xs text-zinc-500 leading-5;
}

/* Селект с иконкой */
.select-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.dropdown-icon {
  position: absolute;
  right: 14px;
  pointer-events: none;
}

.year-display {
  display: flex;
  flex-direction: column;
  gap: 0;
  padding: 8px 14px;
  background: #f1f1f1;
  border-radius: 8px;
  width: 100%;
  height: 53px;
  box-sizing: border-box;
}

.year-label {
  font-size: 16px;
  color: #888;
  line-height: 1;
}

.year-value {
  font-size: 21px;
  color: #222;
  font-weight: 500;
  line-height: 1;
}

/* Секция загрузки фото */
.photo-upload-section {
  @apply flex gap-4 mt-4;
}

.upload-preview {
  @apply w-32 rounded-xl bg-zinc-100;
  @apply flex items-center justify-center;
  svg {
    @apply w-10 h-10;
  }
}

.upload-content {
  @apply flex-1 flex flex-col gap-4;

  .upload-info {
    @apply flex flex-col gap-2;
    .upload-title {
      @apply text-base font-semibold text-black leading-5;
    }
    .upload-description {
      @apply text-sm text-black leading-5;
    }
  }

  .upload-button-section {
    @apply flex flex-col items-start gap-2;

    .upload-button {
      @apply flex items-center gap-2 px-4 py-2 rounded-xl;
      @apply bg-green-600 text-white text-sm font-medium cursor-pointer;

      &:hover {
        @apply bg-green-700;
      }

      .upload-icon {
        @apply w-5 h-5;
      }

      span {
        @apply text-base font-medium text-white;
      }
    }

    .upload-hint {
      @apply text-sm text-zinc-500 leading-5;
    }
  }
}

/* Кнопка отправки */
.submit-button {
  @apply w-full rounded-xl bg-green-600 text-white text-base font-medium cursor-pointer;
  @apply px-4 py-3;
  &:hover {
    @apply bg-green-700;
  }
}

.submit-button:hover {
  background: #047038;
}

/* Кнопка закрытия */
.close-button {
  @apply absolute top-0 right-0 w-6 h-6 bg-transparent border-none cursor-pointer flex items-center justify-center;
}

.close-button svg {
  width: 24px;
  height: 24px;
}

/* Адаптивность */
@media (max-width: 768px) {
  .pilgrim-form {
    width: 100%;
    height: auto;
    padding: 24px 16px;
  }

  .photo-upload-section {
    flex-direction: column;
  }

  .upload-preview {
    width: 100%;
    height: 120px;
  }
}
</style>
